import QueryRenderer from 'relay-query-lookup-renderer';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import React from 'react';
import { createOperationDescriptor, getRequest } from 'relay-runtime';
import serverVars from 'server-vars';
import { ErrorPageContent } from '../components/global/ErrorPageContent/ErrorPageContent';
import { ReactRelayContext } from 'react-relay';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { ServerVarsContextProvider } from '../global/ServerVarsContext/ServerVarsContext';
import { defaultLocale } from 'dibs-intl/exports/constants';
import { QP_SEARCH_QUERY } from '../constants/queryParamConstants';
import {
    markPerfMetric,
    measurePerfMetric,
    PERF_METRICS,
} from 'dibs-buyer-layout/exports/perfTrackingHelpers';
import { UIDProvider } from 'dibs-uid';

export function PrepareRelayContextProvider(props) {
    const { environment, query, variables, Component, ...otherProps } = props;
    const operation = createOperationDescriptor(getRequest(query), variables);
    environment.retain(operation);

    const snapshot = environment.lookup(operation.fragment);
    const { data } = snapshot;

    const renderProps = {
        ...variables,
        ...otherProps,
        ...data,
    };

    return (
        <ReactRelayContext.Provider
            value={{
                environment: environment,
                variables: variables || {},
            }}
        >
            <Component {...renderProps} />
        </ReactRelayContext.Provider>
    );
}

PrepareRelayContextProvider.propTypes = {
    environment: PropTypes.object.isRequired,
    query: PropTypes.func.isRequired,
    variables: PropTypes.object.isRequired,
    Component: PropTypes.elementType.isRequired,
};

export function PrepareRelayQueryRenderer(props) {
    const { environment, query, variables, ignoreErrors, Component, ...otherProps } = props;
    /* eslint-disable react/prop-types */
    return (
        <QueryRenderer
            lookup
            query={query}
            variables={variables}
            environment={environment}
            render={({ error, props: relayData }) => {
                if (error && !ignoreErrors) {
                    return <ErrorPageContent />;
                }
                const renderProps = {
                    /**
                     * TODO INFRA: Probably dont need `isLoading`, but need to be
                     * extra careful removing it, pdp code might depend on it.
                     */
                    isLoading: !!relayData,
                    ...otherProps,
                    ...relayData,
                };
                return <Component {...renderProps} />;
            }}
        />
    );
    /* eslint-enable */
}

PrepareRelayQueryRenderer.propTypes = {
    environment: PropTypes.object.isRequired,
    query: PropTypes.func.isRequired,
    variables: PropTypes.object.isRequired,
    Component: PropTypes.elementType.isRequired,
    ignoreErrors: PropTypes.bool,
};

export function renderEntryComponent(options) {
    const { containerId, store, useQueryRenderer, ...otherProps } = options;
    const searchParams = new URLSearchParams(location.search);

    markPerfMetric(PERF_METRICS.HYDRATE);
    ReactDOM.hydrate(
        <IntlProvider
            locale={serverVars.get('locale')}
            defaultLocale={defaultLocale}
            messages={serverVars.get('messages')}
        >
            <UIDProvider>
                <ServerVarsContextProvider
                    isMobile={serverVars.get('settings.isMobile')}
                    isTablet={serverVars.get('settings.isTablet')}
                    isDesktop={serverVars.get('settings.isDesktop')}
                    isSbAdsEnabled={serverVars.get('isSbAdsEnabled')}
                    clientConf={serverVars.get('config')}
                    localeOrigin={serverVars.get('localeOrigin')}
                    isSearchQueryApplied={!!searchParams.get(QP_SEARCH_QUERY)}
                    isSbTopNavVariant={serverVars.get('isSbTopNavVariant')}
                    isSmallerSbCategoryTilesVariant={serverVars.get(
                        'isSmallerSbCategoryTilesVariant'
                    )}
                >
                    <Provider store={store}>
                        {useQueryRenderer ? (
                            <PrepareRelayQueryRenderer {...otherProps} />
                        ) : (
                            <PrepareRelayContextProvider {...otherProps} />
                        )}
                    </Provider>
                </ServerVarsContextProvider>
            </UIDProvider>
        </IntlProvider>,
        document.getElementById(containerId),
        () => {
            measurePerfMetric(PERF_METRICS.HYDRATE, PERF_METRICS.HYDRATE);
        }
    );
}
